import { useRef, useState, useEffect, useMemo } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, MessageSquare, Users, Circle, FileText, Ticket, Stamp } from "lucide-react";
import { Reveal, Overline, KineticHeading } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { ServerStatus } from "@/components/ServerStatus";
import { useTitle } from "@/components/PageHeader";
import { useSiteConfig } from "@/context/SiteConfig";
import { api } from "@/lib/api";
import { IMAGES, FEATURES, MANIFESTO } from "@/data/content";

const DiscordBand = () => {
  const { home, link } = useSiteConfig();
  const [d, setD] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/discord").then(({ data }) => alive && setD(data)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  const fmt = (n) => (n || 0).toLocaleString();
  return (
    <section className="relative overflow-hidden border-t border-[#2A201A]" data-testid="discord-band">
      <div className="mx-auto max-w-7xl px-6 sm:px-10 py-24 sm:py-32 grid gap-12 lg:grid-cols-2 items-center">
        <Reveal>
          <Overline>Join the community</Overline>
          <h2 className="mt-6 font-display text-4xl sm:text-6xl font-light tracking-tight leading-none">
            {d?.name || "Aurora Springs"} on Discord
          </h2>
          <p className="mt-6 max-w-lg text-[#8F877C] leading-relaxed">
            {home?.discord_sub || "Get whitelisted, meet the community, follow the story and never miss an event. The whole frontier gathers here."}
          </p>
          <a href={link("discord")} target="_blank" rel="noreferrer" data-testid="discord-join-btn"
            className="mt-8 inline-flex items-center gap-2 font-feature text-sm uppercase tracking-widest bg-teal text-obsidian px-8 py-4 hover:bg-bone transition-colors">
            <MessageSquare className="h-4 w-4" /> Join the Discord
          </a>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="border border-[#2A201A] bg-surface/50 backdrop-blur-sm p-8 sm:p-10 aurora-glow">
            <div className="flex items-center gap-4">
              {d?.icon_url && <img src={d.icon_url} alt="" className="h-14 w-14 rounded-full ring-1 ring-gold/40" />}
              <div>
                <p className="font-display text-2xl">{d?.name || "Aurora Springs RedM"}</p>
                <p className="font-mono text-xs text-[#8F877C]">{link("discord").replace(/^https?:\/\//, "")}</p>
              </div>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="border border-[#2A201A] bg-obsidian p-6" data-testid="discord-online">
                <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-teal"><Circle className="h-2.5 w-2.5 fill-teal" /> Online</span>
                <p className="mt-2 font-display text-4xl">{d ? fmt(d.online) : "—"}</p>
              </div>
              <div className="border border-[#2A201A] bg-obsidian p-6" data-testid="discord-members">
                <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#8F877C]"><Users className="h-3.5 w-3.5" /> Members</span>
                <p className="mt-2 font-display text-4xl">{d ? fmt(d.members) : "—"}</p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const EventsBand = () => {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/discord/events").then(({ data }) => alive && setEvents(data.events || [])).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  if (!events.length) return null;
  const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };
  return (
    <section className="mx-auto max-w-7xl px-6 sm:px-10 py-24 sm:py-32 border-t border-[#2A201A]" data-testid="events-band">
      <Reveal><Overline>Upcoming events</Overline>
        <h2 className="mt-6 font-display text-4xl sm:text-6xl font-light tracking-tight leading-none">What's happening on the frontier</h2>
      </Reveal>
      <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {events.slice(0, 6).map((e, i) => (
          <Reveal key={e.id} delay={(i % 3) * 0.06}>
            <a href={e.url} target="_blank" rel="noreferrer" data-testid={`event-card-${i}`}
              className="group block overflow-hidden border border-[#2A201A] bg-surface/40 h-full hover:border-teal/40 transition-colors">
              {e.cover && <div className="relative h-40 overflow-hidden"><img src={e.cover} alt={e.name} className="h-full w-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700" /></div>}
              <div className="p-7">
                <p className="font-mono text-xs uppercase tracking-widest text-teal">{fmt(e.start_time)}</p>
                <h3 className="mt-3 font-display text-2xl font-light">{e.name}</h3>
                {e.location && <p className="mt-1 font-mono text-xs text-[#8F877C]">{e.location}</p>}
                {e.description && <p className="mt-3 text-sm text-[#8F877C] leading-relaxed line-clamp-3">{e.description}</p>}
              </div>
            </a>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

// deterministic pseudo-random starfield (computed once at module load)
const STARS = Array.from({ length: 42 }, (_, i) => {
  const r = (n) => { const v = Math.sin(i * 127.1 + n * 311.7) * 43758.5453; return v - Math.floor(v); };
  return { x: (r(1) * 100).toFixed(1), y: (r(2) * 100).toFixed(1), s: r(3) < 0.7 ? 1 : 2, d: (2.5 + r(4) * 4).toFixed(1), dl: (-r(5) * 6).toFixed(1) };
});

const Hero = () => {

  const { img, home, link } = useSiteConfig();
  const h = home || {};
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  // aurora video: static artwork shows instantly, then hands off once playing;
  // reduced-motion visitors keep the static artwork
  const reduceMotion = useMemo(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches, []);
  const [videoOk, setVideoOk] = useState(false);
  return (
    <section ref={ref} className="relative h-screen min-h-[720px] overflow-hidden" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0 -z-10">
        <img src={img("hero", IMAGES.hero)} alt="" aria-hidden="true"
          fetchpriority="high" loading="eager" decoding="async"
          onError={(e) => { if (e.currentTarget.src !== IMAGES.hero) e.currentTarget.src = IMAGES.hero; }}
          style={{ filter: "brightness(1.14)" }}
          className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/50 to-obsidian/70" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_20%,rgba(63,199,173,0.16),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_80%,rgba(199,159,91,0.12),transparent_50%)]" />
        {/* faint twinkling starfield across the night sky */}
        <div className="hero-starfield" aria-hidden="true" data-testid="hero-starfield">
          {STARS.map((s, i) => (
            <div key={i} className="star" style={{
              left: `${s.x}%`, top: `${s.y}%`, width: s.s, height: s.s,
              animationDuration: `${s.d}s`, animationDelay: `${s.dl}s`,
            }} />
          ))}
        </div>
        {/* Northern Lights video loop — seamless mirrored encode (no visible
            loop end), black sky vanishes via screen blend; static artwork shows
            while loading and for reduced-motion visitors */}
        <div className="aurora-sky" aria-hidden="true" data-testid="hero-aurora-sky">
          {(!videoOk || reduceMotion) && (
            <img src="/assets/aurora-wave.webp" alt="" className="aurora-layer" />
          )}
          {!reduceMotion && (
            <video className="aurora-layer" data-testid="hero-aurora-video"
              autoPlay muted loop playsInline preload="auto"
              onLoadedMetadata={(e) => { e.currentTarget.playbackRate = 0.7; }}
              onPlaying={() => setVideoOk(true)}>
              <source src="/assets/aurora-v3.webm" type="video/webm" />
              <source src="/assets/aurora-v3.mp4" type="video/mp4" />
            </video>
          )}
        </div>
        {/* rare shooting stars streaking across the sky */}
        <div className="hero-stars" aria-hidden="true" data-testid="hero-shooting-stars">
          <div className="shooting-star a" />
          <div className="shooting-star b" />
        </div>
      </motion.div>
      <motion.div style={{ opacity }} className="relative z-10 mx-auto max-w-7xl px-6 sm:px-10 h-full flex flex-col justify-center">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
          <ServerStatus className="mb-8" />
        </motion.div>
        <KineticHeading
          className="font-display font-light tracking-tighter leading-[0.85] text-6xl sm:text-7xl lg:text-8xl xl:text-9xl text-shadow-hero"
          lines={[h.hero_line1 || "Ride into the", <span key="a" className="italic text-teal">{h.hero_highlight || "Aurora Springs"}</span>, h.hero_line3 || "frontier."]}
        />
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 max-w-xl text-base sm:text-lg text-bone/80 leading-relaxed text-shadow-hero">
          {h.hero_sub || "A whitelisted RedM roleplay world where every choice carves your legend into the dust. Become someone. Stay a while."}
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-4">
          <a href={link("connect")} target="_blank" rel="noreferrer" data-testid="hero-connect-btn"
            className="group inline-flex items-center gap-2 font-feature text-sm uppercase tracking-widest bg-teal text-obsidian px-8 py-4 hover:bg-bone transition-colors">
            Direct Connect <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
          <a href={link("discord")} target="_blank" rel="noreferrer" data-testid="hero-discord-btn"
            className="group inline-flex items-center gap-2 font-feature text-sm uppercase tracking-widest border border-[#3a2f27] text-bone px-8 py-4 hover:border-teal hover:text-teal transition-colors">
            <MessageSquare className="h-4 w-4" /> Join Discord
          </a>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.8 }}
          className="mt-4 flex flex-wrap gap-3">
          {link("countyApp") && (
            <a href={link("countyApp")} target="_blank" rel="noreferrer" data-testid="hero-county-app-btn"
              className="group inline-flex items-center gap-2 font-feature text-[11px] uppercase tracking-widest border border-[#2A201A] bg-surface/40 text-[#c9c0b4] px-4 py-2.5 hover:border-teal hover:text-teal transition-colors">
              <Stamp className="h-3.5 w-3.5" /> County Applications
            </a>
          )}
          {link("whitelistApp") && (
            <a href={link("whitelistApp")} target="_blank" rel="noreferrer" data-testid="hero-whitelist-app-btn"
              className="group inline-flex items-center gap-2 font-feature text-[11px] uppercase tracking-widest border border-[#2A201A] bg-surface/40 text-[#c9c0b4] px-4 py-2.5 hover:border-teal hover:text-teal transition-colors">
              <FileText className="h-3.5 w-3.5" /> Whitelist Application
            </a>
          )}
          {link("ticket") && (
            <a href={link("ticket")} target="_blank" rel="noreferrer" data-testid="hero-ticket-btn"
              className="group inline-flex items-center gap-2 font-feature text-[11px] uppercase tracking-widest border border-[#2A201A] bg-surface/40 text-[#c9c0b4] px-4 py-2.5 hover:border-teal hover:text-teal transition-colors">
              <Ticket className="h-3.5 w-3.5" /> Open Ticket
            </a>
          )}
        </motion.div>
      </motion.div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.4em] text-[#8F877C] animate-pulse-dot">Scroll</div>
    </section>
  );
};

const Manifesto = () => {
  const { img, home, link } = useSiteConfig();
  const items = (home?.creed?.length ? home.creed : MANIFESTO);
  return (
  <section className="mx-auto max-w-7xl px-6 sm:px-10 py-28 sm:py-40" data-testid="manifesto">
    <Reveal><Overline>{home?.creed_label || "The Creed / 001"}</Overline></Reveal>
    <div className="mt-16 grid gap-x-16 gap-y-20 lg:grid-cols-2 items-start">
      <div className="space-y-20">
        {items.map((m, i) => (
          <Reveal key={m.n} delay={i * 0.1}>
            <div className="flex gap-6">
              <span className="font-mono text-teal text-sm pt-2">{m.n}</span>
              <div>
                <h3 className="font-display text-3xl sm:text-4xl font-light">{m.head}</h3>
                <p className="mt-4 text-[#8F877C] leading-relaxed max-w-md">{m.body}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.15} className="lg:sticky lg:top-28">
        <div className="relative overflow-hidden">
          <img src={img("manifesto", IMAGES.manifesto)} alt="Lone rider" className="w-full h-[600px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 to-transparent" />
          <div className="absolute bottom-6 left-6 font-mono text-xs uppercase tracking-widest text-teal">Est. under the northern lights</div>
        </div>
      </Reveal>
    </div>
  </section>
  );
};

const Features = () => {
  const { img, home, link } = useSiteConfig();
  const feats = (home?.features?.length ? home.features : FEATURES);
  return (
  <section className="relative py-28 sm:py-40 border-t border-[#2A201A]" data-testid="features">
    <div className="absolute inset-0 -z-10 opacity-50">
      <img src={img("features", IMAGES.aurora)} alt="" className="h-full w-full object-cover mask-fade-b" />
      <div className="absolute inset-0 bg-obsidian/50" />
    </div>
    <div className="mx-auto max-w-7xl px-6 sm:px-10">
      <Reveal className="max-w-3xl">
        <Overline>{home?.features_label || "What sets us apart / 002"}</Overline>
        <h2 className="mt-6 font-display text-4xl sm:text-6xl font-light tracking-tight leading-none">{home?.features_heading || "Built for the story you haven't told yet."}</h2>
      </Reveal>
      <div className="mt-16 grid gap-px bg-[#2A201A] sm:grid-cols-2 lg:grid-cols-4">
        {feats.map((f, i) => (
          <Reveal key={f.n} delay={i * 0.06}>
            <div className="group bg-obsidian p-8 sm:p-10 h-full hover:bg-surface transition-colors duration-500">
              <span className="font-mono text-sm text-teal">{f.n}</span>
              <h3 className="mt-6 font-display text-2xl font-medium">{f.title}</h3>
              <p className="mt-4 text-sm text-[#8F877C] leading-relaxed">{f.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
  );
};

const CTA = () => {
  const { img, home, link } = useSiteConfig();
  return (
  <section className="relative overflow-hidden border-t border-[#2A201A]" data-testid="cta">
    <div className="absolute inset-0 -z-10">
      <img src={img("cta", IMAGES.duskRider)} alt="" className="h-full w-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/60 to-obsidian" />
    </div>
    <div className="mx-auto max-w-4xl px-6 sm:px-10 py-32 sm:py-48 text-center">
      <Reveal>
        <Overline>The gate is open</Overline>
        <h2 className="mt-6 font-display text-5xl sm:text-7xl font-light tracking-tighter leading-[0.9]">
          {(home?.cta_heading || "Your story starts\nthe moment you connect.").split("\n").map((ln, i) => (
            <span key={i}>{i > 0 && <br />}{ln}</span>
          ))}
        </h2>
        <div className="mt-12 flex flex-wrap justify-center gap-4">
          <a href={link("connect")} target="_blank" rel="noreferrer" data-testid="cta-connect-btn"
            className="inline-flex items-center gap-2 font-feature text-sm uppercase tracking-widest bg-teal text-obsidian px-10 py-5 hover:bg-bone transition-colors">Connect to Aurora Springs</a>
          <Link to="/rules" data-testid="cta-rules-btn"
            className="inline-flex items-center gap-2 font-feature text-sm uppercase tracking-widest border border-[#3a2f27] text-bone px-10 py-5 hover:border-teal hover:text-teal transition-colors">Read the Rules</Link>
        </div>
        <p className="mt-8 font-mono text-xs text-[#5c554c]">cfx.re/join/{link("joinCode")}</p>
      </Reveal>
    </div>
  </section>
  );
};

export default function Home() {
  useTitle("Aurora Springs RP | RedM Roleplay Server", "Aurora Springs RP is an 18+ whitelisted RedM roleplay server set in 1895. Explore immersive Wild West roleplay, businesses, law, medical, gangs, tribes, housing, ranching and more.");
  return (
    <>
      <Hero />
      <Marquee />
      <Manifesto />
      <Features />
      <DiscordBand />
      <EventsBand />
      <CTA />
    </>
  );
}
